import { ReadingUnitType, TxtItemType } from "../reading-utils/enums";
import { customJoin, getBeforeLastBlockElement, normalizeText, removeBeforeLastBlockElement } from "./text-processor";
import { TextAtom } from "src/lib/cheshire-cat/models/text-atom.model";
import { ContentBlockClass, ContentBlockTargetFetchMode, EngineEventTargetFrameworkComponent, IContentBlock, IContentBlockTarget, IContentLocation, IMouseEngineEvent, IPointerEngineEvent, IReaderView, IReaderViewTransformManager, ITransformData, IVisiblePage } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { ReadingAtom } from "../reading-utils/interfaces";
import { getXPath } from "./xpath";
import { NodeType } from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";

export interface ContentBlock {
    contentBlock: IContentBlock;
    textMapItems: TextAtom[];
}

export class EpubTextIterator {
    readingUnit: ReadingUnitType = ReadingUnitType.Sentence;
    // all content blocks of that spine element, at the index of that spine element
    private txtMap: TextAtom[][] = [];
    private processedContentBlocks: ContentBlock[][] = [];
    private lastReturnedAtoms: ReadingAtom[] = [];

    // NOTE each contentBlock has a unique id per spine element
    addProcessedContentBlocks(spineItemIndex: number, textMap: TextAtom[], blocks: ContentBlock[]) {
        this.txtMap[spineItemIndex] = textMap
        this.processedContentBlocks[spineItemIndex] = blocks;
        console.log('Processing done.');
    }

    hasProcessed(spineItemIndex: number): boolean {
        return this.processedContentBlocks[spineItemIndex] !== undefined; // undefined if not processed yet
    }

    async getFirstItem(visiblePage: IVisiblePage): Promise<ReadingAtom[]> {
        const indexInSpine = visiblePage.toJSON().readerDocumentIndexInSpine;

        // if there are no content blocks, don't bother
        if (this.processedContentBlocks[indexInSpine].length === 0) {
            return [];
        }

        const location: IContentLocation = visiblePage.getRangeContentLocation();
        // fetch finds EVERY contentBlock, so that includes parent AND (some) children
        const targets: IContentBlockTarget[] = (await location.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING_OR_NEAREST_AFTER));
        if (targets.length === 0) {
            return [];
        }

        const target: IContentBlockTarget = targets[0];
        const block: IContentBlock = target.getContentBlock();
        const first = this.findFirstIContentBlockWithText([block]);
        // if no block with text was found
        if (!first) {
            return [];
        }

        // find the processed corresponding contentBlock
        const processedContentBlock = this.processedContentBlocks[indexInSpine]?.find((x) => x.contentBlock.getId() === first.getId());
        if (!processedContentBlock) {
            return []
        }

        // for when the contentblock is divided over 2 pages
        let atoms = processedContentBlock.textMapItems;
        const charOffset = target.getCharOffset();

        // if the text that is visible is not the beginning of the block
        if (charOffset !== 0) {
            // the content block text has multiple spaces and /n that the textmap does not have
            // so get everything before the offset and trim the white spaces and /n
            let startBlockText = block.getTextContent().slice(0, charOffset).replace(/\s+/g, ' ').trim(); // remove tabs and new lines
            startBlockText = normalizeText(startBlockText); // to make textmap and text the same, like … -> ...
            // then count the new offset based on the cleaned text so we can find it in the text map items
            const newOffset = startBlockText.length + 1; // +1 for space after final word
            // cumulativeOffset, becase we can not use locSymbPos, because that is within a node, not a content block
            // see text-processor for more explenation about logic
            let cumulativeOffset = (startBlockText.match(/ /g) || []).length + 1; // +1 for the space after the last word
            // let nextLocSymbPos = -1; // Track the locSymbPos of the next atom
            const textMapItems = processedContentBlock.textMapItems;
            atoms = textMapItems.filter((atom) => {
                cumulativeOffset += atom.atomText.length;
                return cumulativeOffset > newOffset;
            });
        }
        console.log('start reading from first: ', atoms[0]);
        return this.buildReadingAtoms(0, atoms, indexInSpine);
    }

    hasNextItem(): boolean {
        const lastSpokenAtom = this.lastReturnedAtoms.find((x) => x.isLast);
        if (!lastSpokenAtom) {
            return false;
        }

        return this.txtMap[lastSpokenAtom.pageIdx].length > lastSpokenAtom.itemIdx;
    }

    getNextItem(): ReadingAtom[] {
        const lastSpokenAtom = this.lastReturnedAtoms.find((x) => x.isLast);
        if (!lastSpokenAtom) {
            return [];
        }

        const txtMap = this.txtMap[lastSpokenAtom.pageIdx]; // pageIdx is the spine index

        // is there a next atom
        if (lastSpokenAtom.itemIdx + 1 < txtMap.length) {
            return this.buildReadingAtoms(lastSpokenAtom.itemIdx + 1, txtMap, lastSpokenAtom.pageIdx);
        }

        return this.findFirstItemNextSpine(lastSpokenAtom.pageIdx + 1);
    }

    getPreviousItem(): void {
        // TODO
        switch (this.readingUnit) {
            case ReadingUnitType.Word: break;
            case ReadingUnitType.Sentence: break;
        }
    }

    getLocation(words: ReadingAtom[]): ILocator | undefined {
        if (words.length === 0) {
            console.error('Get location - no words passed.');
            return;
        }

        // words should not be in different content blocks
        // 7 APR 2026 -> words are in different content blocks !! (since CCT-6 - span fix)
        const firstWord = words[0];
        const blocks = this.processedContentBlocks[firstWord.pageIdx].filter((block) =>
            words.some((word) =>
                block.textMapItems.some((item) => item.itemId === word.itemIdx)
            )
        );

        if (blocks.length === 0) {
            console.error('Get location - no content block found for atom: ', firstWord);
            return;
        }

        const contentLocation = blocks.length === 1 ? this.getLocationSingleBlock(words, blocks[0]) : this.getLocationMultipleBlocks(words, blocks);
        return contentLocation?.getLocator();
    }

    async scrollToReadingAtom(atom: ReadingAtom, readerView: IReaderView): Promise<void> {
        if (!atom) {
            return;
        }
        const block = this.processedContentBlocks[atom.pageIdx].find((x) => x.textMapItems.some((y) => y.itemId === atom.itemIdx));
        if (!block) {
            console.error('Scroll to word - no content block found for atom: ', atom);
            return;
        }

        const visiblePages = readerView.getVisiblePages();
        const isBlockVisible = visiblePages.flatMap((x) => x.getRangeContentLocation()).some((x) => x.contains(block.contentBlock.getContentLocation()));
        if (readerView.canPerformGoTo() && !isBlockVisible) {
            // getContentLocation takes a charOffset, which is important for blocks that are split over 2 pages
            const charOffset = atom.itemIdx !== block.textMapItems[0].itemId
                ? block.textMapItems.filter((x) => x.itemId <= atom.itemIdx).map((x) => x.atomText).join(' ').length
                : 0;
            await readerView.goTo(block.contentBlock.getContentLocation(charOffset));
        }
    }

    async getItemsByClick(click: IMouseEngineEvent, readerView: IReaderView): Promise<ReadingAtom[]> {
        if (!click.target) {
            console.error('Get Reading items by click - no target found.');
            return [];
        }

        // NOTE 25 NOV 2025
        // if the user clicked on a comment (annotation), the target.contentLocation is completely wrong
        // !!!!!!!!!!!!!
        const frameworkComponent = click.target.frameworkComponent;
        if (
            // The app window, or some element outside the ReaderView was the target of the event.
            // This value is only used if the ReaderView option `constrainEngineEventsToReaderView` is set to false.
            frameworkComponent === EngineEventTargetFrameworkComponent.APP_WINDOW ||
            // The ReaderView was the target of the event, but it was outside a ReaderDocument or ReaderViewAnnotation.
            // For example used when clicking outside a reader document, but inside the ReaderView element.
            frameworkComponent === EngineEventTargetFrameworkComponent.READER_VIEW
        ) {
            // if the user didn't click on the document, then they definitely didn't click on any text
            return [];
        }

        // *** FIRST ***
        // find the iframe that the user clicked in
        // range is needed for both TEXT and ANNOTATION
        const range = this.findRangeInFrame(readerView, click.clientX, click.clientY);

        // if no range found, no point in continuing
        // TODO, do we need to find an alternative if no range is found?
        if (!range) {
            console.error('Get Reading items by click - no range found.');
            return [];
        }

        // *** SECOND ***
        // find the index of the spine item that is open
        // spineIndex and blocks are needed for both TEXT and ANNOTATION
        const spineIndex = click.toJSON().target?.readerDocumentIndexInSpine;
        if (spineIndex === null || spineIndex === undefined) {
            console.error('Get Reading items by click - no spineIndex found.');
            return [];
        }
        const blocks = this.processedContentBlocks[spineIndex];

        // *** THIRD *** 
        // try to find the contentBlock the user clicked
        // This is different for Text and Annotation
        // NOTE ! if a <p> is split up by <br>, the first contentblock is always returned!
        let txtAtomsFromContentBlock: TextAtom[] = [];
        let blocksWithSameAtoms: ContentBlock[] = [];
        const pageCoord = { x: click.pageX, y: click.pageY };
        const page = this.getClickedVisiblePage(readerView, pageCoord)

        if (frameworkComponent === EngineEventTargetFrameworkComponent.READER_DOCUMENT) {
            const targetLocation: IContentLocation | null | undefined = click.target.contentLocation;
            if (!targetLocation || targetLocation === null) {
                console.error('Get Reading items by click - no target of contentLocation found.');
                return [];
            }

            const contentBlockTargets: IContentBlockTarget[] = await targetLocation.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING_OR_NEAREST_AFTER);
            const contentBlocks: IContentBlock[] = contentBlockTargets.map((x) => x.getContentBlock());

            // then find the corresponding custom contentblock in the list of custom blocks via the IContentBlock from the click
            // -> contentBlocks[Ø] TODO can it be more then one on click?
            // -> it can have a child, but can it havel multiple?
            // and when the custom contentblock is found, find the text atoms and use the range from the iframe to find the offset
            // and therefore the exact text atom that was clicked on
            txtAtomsFromContentBlock =
                (contentBlocks[0].getTextContent() !== ''
                    ? blocks.find((x) => x.contentBlock.getId() === contentBlocks[0].getId())?.textMapItems
                    : blocks.find((x) => contentBlocks[0].getChildren().map((y) => y.getId()).includes(x.contentBlock.getId()))?.textMapItems
                ) ?? [];
            if (txtAtomsFromContentBlock.length === 0) {
                console.error('Get Reading items by click - no atoms found for this content block: ', contentBlocks.map((x) => x.toSerializableData()));
                return [];
            }

            // are there any other blocks that are also in that element?
            blocksWithSameAtoms = this.filterBlocksWithSameAtoms(blocks, txtAtomsFromContentBlock[0].xpath);

            // when a <p> has <br/> in it
            // then colibrio will divide that into different content blocks
            // but when clicked on, target will always point to the first one

            // use the x and y to search through the rectangles of those content blocks
            if (blocksWithSameAtoms.length > 1) {
                if (page) {
                    const block = await this.getClickedOnBlockFromPage(page, blocksWithSameAtoms, pageCoord);
                    if (block) {
                        txtAtomsFromContentBlock = block.textMapItems;
                    }
                }
            }
        } else if (frameworkComponent === EngineEventTargetFrameworkComponent.READER_VIEW_ANNOTATION) {
            if (page) {
                const block = await this.getClickedOnBlockFromPage(
                    page,
                    blocks.filter((x) => x.contentBlock.getBlockClass() !== ContentBlockClass.CONTAINER),
                    pageCoord
                );
                if (block) {
                    txtAtomsFromContentBlock = block.textMapItems;
                    blocksWithSameAtoms = [block];
                }
            }
        }

        // somethimes, the <p> is also split over more pages
        // so check if all blocks are on this visible page
        // if not, then offset is off for items that are on the 'second' page
        // for the first part 'getTxtAtomsFromNode' will have no issues
        // so if the first block is not on the visible page
        // if page is undefined, result will be false (false should be default)
        const needSpecialCaseSearchInNode = !page?.getRangeContentLocation().contains(blocksWithSameAtoms[0].contentBlock.getContentLocation());

        // if content has <span> for example, range.offset will be within that span
        // use similar logic to caliban to find the exact word in that case
        let filteredTxtAtomsFromContentBlock: TextAtom[] = needSpecialCaseSearchInNode
            ? this.getTxtAtomsFromNodeText(range.startContainer, txtAtomsFromContentBlock)
            : this.getTxtAtomsFromNode(range.startContainer, txtAtomsFromContentBlock);
        if (filteredTxtAtomsFromContentBlock.length === 0) {
            console.error('Get Reading items by click - no text atoms found.');
            return [];
        }

        // if first atom from txtAtomsFromContentBlock is not the same as filteredTxtAtomsFromContentBlock
        // then contentblock was spread over 2 pages and offset needs adjusting
        // and contentblocks can have several nodes inside, so compare to first word of the node
        const firstAtomFromNode = txtAtomsFromContentBlock.find((x) => x.xpath === filteredTxtAtomsFromContentBlock[0].xpath);
        let offset = range.startOffset
            + (filteredTxtAtomsFromContentBlock.length > 0 && firstAtomFromNode && firstAtomFromNode.itemId !== filteredTxtAtomsFromContentBlock[0].itemId
                ? (filteredTxtAtomsFromContentBlock[0].locSymbPos + 1) : 0); // +1 for the missing space

        // somethimes, characters are counted wrong, because of this for example
        // .replace(/…/g, "...")
        // so check that
        const rangeText = range.startContainer.textContent?.slice(0, offset)
        if (rangeText && normalizeText(rangeText).length !== rangeText.length) {
            offset = normalizeText(rangeText).length;
        }

        // find the txtAtom that wax clicked on
        const txtAtom =
            filteredTxtAtomsFromContentBlock.find(
                (x) => offset >= x.locSymbPos && offset <= x.locSymbPos + x.atomText.length
            ) || null;

        if (txtAtom === null) {
            console.error('Get Reading items by click - no text atom found.');
            return [];
        }

        // continue to read from there
        console.log('start reading from: ', txtAtom);
        const txtMap = this.txtMap[spineIndex];
        return this.buildReadingAtoms(txtAtom.itemId, txtMap, spineIndex);
    }

    async getLinkItemsByHover(move: IPointerEngineEvent, readerView: IReaderView): Promise<ReadingAtom[]> {
        const range = this.findRangeInFrame(readerView, move.clientX, move.clientY);
        if (!range) {
            console.error('Get Link reading items by hover - no range found.');
            return [];
        }

        const pageCoord = { x: move.pageX, y: move.pageY };
        const page = this.getClickedVisiblePage(readerView, pageCoord);
        if (!page) {
            console.error('Get Link reading items by hover - no page found.');
            return [];
        }

        const spineIndex = page.toJSON().readerDocumentIndexInSpine;
        const blocks = this.processedContentBlocks[spineIndex];
        const block = await this.getClickedOnBlockFromPage(
            page,
            blocks.filter((x) => x.contentBlock.getBlockClass() !== ContentBlockClass.CONTAINER),
            pageCoord
        );
        if (!block) {
            console.error('Get Link reading items by hover - no block found.');
            return [];
        }

        const atoms = this.getTxtAtomsFromNode(range.startContainer, block.textMapItems);
        console.log('start reading link from: ', atoms[0]);
        const txtMap = this.txtMap[spineIndex];

        // web-to-speech always reads the entire link, even when it's word by words
        // so use the same logic here
        const firstItem = txtMap[atoms[0].itemId];
        const sentenceItems = txtMap.filter((x) => x.sentenceId === firstItem.sentenceId && x.itemId >= firstItem.itemId);
        return this.convertAtoms(sentenceItems, spineIndex);
    }

    private findRangeInFrame(readerView: IReaderView, clientX: number, clientY: number): Range | null | undefined {
        const iframes: NodeListOf<HTMLIFrameElement> | undefined = readerView.getElement()?.querySelectorAll('iframe');
        if (!iframes) {
            console.error('Get Reading items by click - no iframes found to search');
            return null;
        }

        const visibleIframes = Array.from(iframes).filter((x) => x.getAttribute('aria-hidden') === null);
        const iframe = visibleIframes.find((x) => this.isClickedInside(x.getBoundingClientRect(), { x: clientX, y: clientY }));

        // if no iframe found, no point in continuing
        if (!iframe) {
            console.error('Get Reading items by click - no single iframe found. ', visibleIframes);
            return null;
        }

        // then with the iframe, try to find the node the user clicked
        const transformMngr: IReaderViewTransformManager = readerView.getTransformManager();
        const data: ITransformData | null = transformMngr.getActiveTransform();
        const scale = data?.scale ?? 1;
        const iframeRect = iframe.getBoundingClientRect();
        const x = (clientX - iframeRect.x) / scale;
        const y = (clientY - iframeRect.y) / scale;
        return iframe.contentDocument?.caretRangeFromPoint(x, y); // NOTE: no alternative yet
    }

    private isClickedInside(rect: DOMRect, coord: { x: number, y: number }): boolean {
        return coord.x >= rect.left &&
            coord.x <= rect.right &&
            coord.y >= rect.top &&
            coord.y <= rect.bottom;
    }

    private getTxtAtomsFromNode(node: Node, txtAtoms: TextAtom[]): TextAtom[] {
        const parent = node.parentElement || null;
        if (parent === null) {
            return [];
        }

        // getXPath in xpath.ts is copy-pasted from CalibanV3
        const parentXpath = getXPath(parent);

        let textNodesIndex = 1;
        for (const n of Array.from(parent.childNodes)) {
            if (n.isSameNode(node)) {
                break;
            }

            if (n.nodeType === NodeType.TEXT) {
                textNodesIndex++;
            }
        }

        // don't look at the entire xpath, because textmap was created from the file in the epub
        // while parent is from the visible html
        // they are similar, but not the same
        const xpath = removeBeforeLastBlockElement(parentXpath + '/') + `text()[${textNodesIndex}]`;
        const result = txtAtoms.filter((x) => removeBeforeLastBlockElement(x.xpath) === xpath);

        // somethimes when a contentblock is split over a page, we'll have way more items in result then there are words in the node
        // if that is the case, then the offset is way off
        let nodeWords = (node.textContent ?? '').trim().split(/\s+/)
        if (nodeWords.length > 0 && result.length > 0) {
            // somethimes punctuation marks are part of a word, and somethimes they are separate
            nodeWords = nodeWords[0] !== result[0].atomText ? nodeWords.filter(x => !/^[\p{P}\s]+$/u.test(x)) : nodeWords;
            // the offset is only a problem if it was clicked on a word on the second part of the content
            if (nodeWords[0] !== result[0].atomText) {
                console.log('first word of items was not first word of node (slice was needed). node: ', node);
                return result.slice(-nodeWords.length, result.length);
            }
        }

        // somethimes it can look like this
        // <h1><a id="sec14" />Why Emotional Self-Awareness?</h1>
        // in that case, the a will be included in the xpath of the atoms
        // while parentXpath will not find it
        // so if a parent has the text node and an empty a
        // adjust xpath and check again
        // NOTE
        // The <a> tag is self-closing, but lxml treats it as an opening tag with implied content.
        // This means the text 'CASE STUDY #2: 15 YEARS TO GRADUATE' is considered part of the <a> element.
        // so somethimes it will be a and somethimes a[2]
        if (
            result.length === 0 &&
            parent.childNodes.length === 2 &&
            Array.from(parent.childNodes).some((child) => child.isSameNode(node)) &&
            Array.from(parent.childNodes).some(
                (child) =>
                    child.nodeName.toLowerCase() === 'a' &&
                    (!child.textContent || child.textContent.trim() === '')
            )
        ) {
            return txtAtoms.filter((x) => {
                const path = removeBeforeLastBlockElement(x.xpath);
                return (path === 'a/' + xpath || path === 'a[2]/' + xpath);
            });
        }

        // NOTE 24 OCT 2025
        // book: Alice's Abenteuer im Wunderland - Lewis Carroll
        // <p xmlns="http://www.w3.org/1999/xhtml" style="margin-top: 0px !important;">
        //    »Ich bin sicherlich nicht Ida,« sagte sie, ... das ist
        //    <a id="Page_19" class="pageno" title="19"></a>
        //     Alles falsch! Ich muß in Clara verwandelt sein! ... wie sonst: —
        // </p>
        // also returns this as path
        // txtAtoms[0].xpath: '/html/body/p[18]/a[1]/text()[1]'
        // txtAtoms[txtAtoms.length - 1].xpath: '/html/body/p[18]/a[2]/text()[1]'
        if (
            result.length === 0 &&
            Array.from(parent.childNodes).some((child) => child.isSameNode(node)) &&
            Array.from(parent.childNodes).some(
                (child) =>
                    child.nodeName.toLowerCase() === 'a' &&
                    (!child.textContent || child.textContent.trim() === '')
            )
        ) {
            let TxtNodesIndex = 1;
            let linkIndex = 1;
            for (const n of Array.from(parent.childNodes)) {
                if (n.isSameNode(node)) {
                    break;
                }

                if (n.nodeType === NodeType.TEXT) {
                    TxtNodesIndex++;
                }

                if (n.nodeType === NodeType.XML_ELEMENT) {
                    if (n.nodeName === 'a') {
                        linkIndex++;
                        TxtNodesIndex = 1;
                    }
                }
            }

            const tmpXpath = removeBeforeLastBlockElement(parentXpath + '/') + `a[${linkIndex}]/text()[${TxtNodesIndex}]`
            return txtAtoms.filter((x) => removeBeforeLastBlockElement(x.xpath) === tmpXpath);
        }

        // but if the atoms are the same as node content, just return result
        return result;
    }

    private getTxtAtomsFromNodeText(node: Node, txtAtoms: TextAtom[]): TextAtom[] {
        const nodeText = node.textContent?.replace(/\s+/g, ' ').trim() ?? ''; // Normalize whitespace
        const numberOfNodeWords = nodeText.split(' ').length
        const numberOfConsecutiveAtoms = numberOfNodeWords < 3 ? numberOfNodeWords : 3; // Number of consecutive atoms to check for, 3 is choosen at random
        let result: TextAtom[] = [];
        let foundThreeConsecutive = false; // Flag to indicate when number of consecutive atoms are found

        for (const atom of txtAtoms) {
            // Check if the atom's text matches exactly
            if (new RegExp(`(^|\\W)${atom.atomText.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\W|$)`).test(nodeText)) {
                // if there already is something in result and we've passed the x consecutive atoms check, just keep adding
                // if there is already something in result, check if the atom is next in the sequence
                if (result.length === 0 || foundThreeConsecutive || (result[result.length - 1].itemId === atom.itemId - 1)) {
                    // the words are actually in the nodeText consecutively
                    const newText = result.map((x) => x.atomText).join(' ') + ` ${atom.atomText}`;
                    // but also don't compate entire nodeText to prevent repetitive sentences in the same nodeText
                    // and don't check anymore if number of consecutive are already found
                    if (!foundThreeConsecutive && !nodeText.slice(0, newText.length * 2).includes(newText)) {
                        result = [];
                    }
                    result.push(atom);
                }
                // if not next in sequence, reset the result
                else {
                    result = [];
                }

                // Set the flag to true after finding x consecutive atoms
                if (result.length === numberOfConsecutiveAtoms) {
                    foundThreeConsecutive = true;
                }
            } else if (foundThreeConsecutive) {
                // Stop adding atoms once the inclusion check fails after finding 3 consecutive atoms
                break;
            } else {
                // if word not in the nodeText, reset the result so it's empty again
                result = [];
            }
        }
        return result;
    }

    private filterBlocksWithSameAtoms(blocks: ContentBlock[], xpath: string): ContentBlock[] {
        return blocks.filter((x) => x.textMapItems.some((y) => getBeforeLastBlockElement(y.xpath) === getBeforeLastBlockElement(xpath)));
    }

    private getClickedVisiblePage(readerView: IReaderView, coord: { x: number, y: number }): IVisiblePage | undefined {
        // get the visible pages
        const visiblePages: IVisiblePage[] = readerView.getVisiblePages();
        // get the page that was clicked on based on it's rect
        const page: IVisiblePage | undefined = visiblePages.find((x) => {
            const rect = x.getBoundingClientRect();
            if (!rect) return false;
            return this.isClickedInside(rect, { x: coord.x, y: coord.y });
        });
        return page;
    }

    private async getClickedOnBlockFromPage(page: IVisiblePage, blocks: ContentBlock[], coord: { x: number, y: number }): Promise<ContentBlock | undefined> {
        //  because you can't use find with async
        const results = await Promise.all(
            blocks.map(async (block) => {
                const rectRes = await page.fetchRectsForVisibleContent(block.contentBlock.getContentLocation()).catch(() => { return undefined });
                const clickedRect = rectRes?.rects.find((x) => this.isClickedInside(x.clientRect, coord));
                return clickedRect ? block : undefined;
            })
        );
        return results.find((b) => b !== undefined);
    }

    private findFirstIContentBlockWithText(blocks: IContentBlock[]): IContentBlock | undefined {
        // get the first one
        const block = blocks[0];
        // as far as I've seen, a block either has children or text
        if (block.getChildren().length > 0) {
            return this.findFirstIContentBlockWithText(block.getChildren());
        }
        return blocks.find((x) => x.getTextContent() !== '');
    }

    private findFirstItemNextSpine(spineIndex: number): ReadingAtom[] {
        // check if there is a next spine element
        const nextSpine = this.processedContentBlocks[spineIndex];
        if (nextSpine) {
            // find the first contentBlock that has text
            const first = nextSpine.find((x) => x.textMapItems.length > 0);
            if (!first) {
                // if there was no text in this spine element, try the next?
                return this.findFirstItemNextSpine(spineIndex + 1);
            }

            return this.buildReadingAtoms(0, first.textMapItems, spineIndex);
        }

        // if not, then no more items
        return [];
    }

    private getLocationSingleBlock(words: ReadingAtom[], block: ContentBlock): IContentLocation | undefined {
        const firstWord = words[0];
        // get the text content, with the normalize, but keep all next line, spaces, ...
        // because they are important for the start index
        const textContent = normalizeText(block.contentBlock.getTextContent());

        // 1. try to determine the start position
        const prevWords = block.textMapItems.filter((x) => x.itemId < firstWord.itemIdx);
        let cumulativePrevText = '';
        let textContentWithoutCumulative = textContent.replace(cumulativePrevText, '');
        prevWords.forEach((x) => {
            const text = normalizeText(x.atomText);
            // if what is left to check, start with anything other then a word
            const match = textContentWithoutCumulative.match(/^\s+/);
            cumulativePrevText += match ? match[0] + text : text
            // then remove what was just processed from the textContent that is left
            textContentWithoutCumulative = textContent.replace(cumulativePrevText, '');
        });

        // any final space or something to add?
        const match = textContentWithoutCumulative.match(/^\s+/);
        if (match) {
            cumulativePrevText += match ? match[0] : '';
            textContentWithoutCumulative = textContent.replace(cumulativePrevText, '');
        }

        // start position found!
        let charOffset = cumulativePrevText.length;

        // but the normalize will make a difference in the final result,
        // because for example
        // .replace(/…/g, "...")
        // will become 3 characters instead of one
        while (normalizeText(block.contentBlock.getTextContent().slice(0, charOffset)) !== cumulativePrevText && charOffset > 0) {
            charOffset -= 1;
        }

        // 2. try to determine the length
        const textContentEnd = textContentWithoutCumulative;
        let cumulativeText = '';
        words.forEach((x) => {
            const text = normalizeText(x.text);
            const match = textContentWithoutCumulative.match(/^\s+/);
            cumulativeText += match ? match[0] + text : text;
            textContentWithoutCumulative = textContentEnd.replace(cumulativeText, '');
        });
        let charLength = cumulativeText.length;

        // but the normalize will make a difference in the final result,
        // because for example
        // .replace(/…/g, "...")
        // will become 3 characters instead of one
        while (normalizeText(block.contentBlock.getTextContent().slice(charOffset, charOffset + charLength)) !== cumulativeText && charLength > 0) {
            charLength -= 1;
        }

        // just in case you reach a situation where charLength is 0
        // (charOffset can be 0 if it's the first word)
        if (charLength === 0) {
            console.error('Can not highlight item, length is 0. Words: ', words);
        }

        // then put them all together to get the location
        const location: IContentLocation = block.contentBlock.getContentLocation(charOffset, charLength);
        return location;
    }

    private getLocationMultipleBlocks(words: ReadingAtom[], blocks: ContentBlock[]): IContentLocation | undefined {
        const itemIdsFirst = new Set(blocks[0].textMapItems.map((x) => x.itemId));
        const wordsFirst = words.filter((w) => itemIdsFirst.has(w.itemIdx));
        const contentLocationFirst = this.getLocationSingleBlock(wordsFirst, blocks[0]);

        const itemIdsLast = new Set(blocks[blocks.length - 1].textMapItems.map((x) => x.itemId));
        const wordsLast = words.filter((w) => itemIdsLast.has(w.itemIdx));
        const contentLocationLast = this.getLocationSingleBlock(wordsLast, blocks[blocks.length - 1])
        if (!contentLocationFirst || !contentLocationLast) {
            console.error('Get location multiple blocks - could not get location for firts or last block. Words: ', words, blocks);
            return;
        }
        return contentLocationFirst.createRangeTo(contentLocationLast);
    }

    private buildReadingAtoms(startAtomIndex: number, txtMap: TextAtom[], indexInSpine: number): ReadingAtom[] {
        const firstItem = txtMap[startAtomIndex];
        let itemsToReturn: TextAtom[] = [];

        switch (this.readingUnit) {
            case ReadingUnitType.Word:
                itemsToReturn = [firstItem];
                break;
            case ReadingUnitType.Sentence:
                itemsToReturn = txtMap.filter((x) => x.sentenceId === firstItem.sentenceId && x.itemId >= firstItem.itemId);
                break;
        }

        return this.convertAtoms(itemsToReturn, indexInSpine);
    }

    private convertAtoms(textAtoms: TextAtom[], indexInSpine: number): ReadingAtom[] {
        // NOTE 31 MAR 2026
        // after the CCT-6 fix, the issue with the <span> is solved
        // so those words are now part of the sentence
        // but the locSymbPos is still the offset within the element
        // for the TTS, and creating the sentence logic we have, that is an issue
        // so for now, if it's the first word in the array, it can be 0
        // otherwise, if it's 0, calculate it based on the previous words, so it becomes cumulative
        const atoms = textAtoms.reduce<{
            atoms: ReadingAtom[];
            texts: string[];
            joinedText: string;
        }>((acc, x, i) => {
            // if the character is a punctiuation mark, and the previous word is not in the same element,
            // then it should be part of the previous word, because it will be read as part of the word
            const previousAtom = acc.atoms[acc.atoms.length - 1];
            const previousTextAtom = textAtoms[i - 1];
            if (previousAtom && 
                previousTextAtom && 
                previousTextAtom.xpath !== x.xpath &&
                this.isTrailingPunctuation(x.atomText)
            ) {
                previousAtom.text += x.atomText;
                previousAtom.isLast = i === textAtoms.length - 1;

                // Replace the last text entry too, so joinedText stays correct
                acc.texts[acc.texts.length - 1] += x.atomText;
                acc.joinedText = customJoin(acc.texts);

                return acc;
            }

            // if it's the first word, it sould be 0
            // otherwise the word start at the length of what came before, +1 for the space
            // BUT if the word is a symbol and the path is different, don't add 1, because it would be explicitly read anyway
            // it will just be needed for intonation
            const nextJoinedText = customJoin([...acc.texts, x.atomText]);
            const symbPos = nextJoinedText.lastIndexOf(x.atomText);

            // proceed as normal
            acc.atoms.push({
                symbPos,
                itemIdx: x.itemId,
                sntncIndex: x.sentenceId,
                itemType: TxtItemType.Word,
                text: x.atomText,
                lang: '',
                isLast: i === textAtoms.length - 1,
                boundary: null,
                pageIdx: indexInSpine, // use for spine 
            } as ReadingAtom);

            acc.texts.push(x.atomText);
            acc.joinedText = customJoin(acc.texts);

            return acc;

        }, {
            atoms: [],
            texts: [],
            joinedText: '',
        }).atoms;
        this.lastReturnedAtoms = atoms;
        return atoms;
    }

    private isTrailingPunctuation(token: string): boolean {
        return /^[.,!?;:…]+$/.test(token);
    }
}